"use client"

import { TerraLink } from "../../component/TerraLink/TerraLink"
import { SpecialButton } from "../../component/SpecialButton/SpecialButton"
import { ScrollChevron } from "../../component/ScrollChevron/ScrollChevron"
import { Icon, IconType } from "@/components/formatters/MXPIcon"
import { useCallback, useEffect, useRef, useState } from "react"
import { CommonDescription } from "../../component/CommonDescription/CommonDescription"
import { useScreenSize } from "../../utils/hooks/useScreenSize"
import { formatFocusArea } from "../../utils/formatFocusArea"
import { SimpleCarousel } from "../../component/SimpleCarousel/SimpleCarousel"
import "./LayoutMacroWorkflow.css"

import {
  darkTextureWrapper,
  colorSettingDetection,
  renderHeaderConditionally,
} from "../../utils/functions"
import {
  layoutHeaderClasses,
  layoutHeaderBigClasses,
  layoutSubheaderClasses,
  layoutEyebrowClasses,
} from "@/components/tailwindHelpers"

interface WorkflowStep {
  header: string
  description: string
  stepIcon?: IconType
  stepType: string
  stepCTA?: SpecialButton
  image: {
    src: string
    alt: string
    focusArea?: string
  }
  LinkComponent?: React.Component<any, any>
}

export interface LayoutMacroWorkflow {
  eyebrow?: string
  headerElement?: "h1" | "h2"
  header?: string
  subheader?: string
  description?: string
  buttons?: TerraLink[]
  backgroundImageSrc?: string
  presetTheme?:
    | "t2-theme-one"
    | "t2-theme-two"
    | "t2-theme-three"
    | "t2-theme-four"
    | "t2-theme-five"
    | "t2-theme-six"
    | "t2-theme-seven"
  isHeaderBig?: boolean
  workflowSteps?: WorkflowStep[]
  LinkComponent?: React.Component<any, any>
  isLCP?: boolean
}

const PAGER_BASE =
  "text-xs font-bold leading-normal mr-[50px] cursor-pointer py-2.5 px-[15px] rounded uppercase tracking-wider last:mr-0"

const CTA_OVERRIDES = [
  "[&_.t2-button--primary:active]:text-gray-m9 [&_.t2-button--primary:active]:font-bold [&_.t2-button--primary:active]:[text-decoration:inherit]",
  "[&_.t2-button--primary:focus]:text-gray-m9 [&_.t2-button--primary:focus]:font-bold [&_.t2-button--primary:focus]:[text-decoration:inherit]",
  "[&_.t2-button--secondary:active]:text-black [&_.t2-button--secondary:active]:font-bold [&_.t2-button--secondary:active]:[text-decoration:inherit]",
  "[&_.t2-button--secondary:focus]:text-black [&_.t2-button--secondary:focus]:font-bold [&_.t2-button--secondary:focus]:[text-decoration:inherit]",
].join(" ")

export const LayoutMacroWorkflow = ({
  eyebrow,
  header,
  headerElement = "h2",
  subheader,
  description,
  buttons,
  backgroundImageSrc,
  presetTheme,
  isHeaderBig,
  workflowSteps,
  LinkComponent,
  isLCP = false,
}: LayoutMacroWorkflow) => {
  const { width } = useScreenSize()
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [showLeftScroll, setShowLeftScroll] = useState(false)
  const [showRightScroll, setShowRightScroll] = useState(false)
  const scrollRafRef = useRef<number | null>(null)
  const rightScrollButtonRef = useRef<HTMLButtonElement>(null)
  const leftScrollButtonRef = useRef<HTMLButtonElement>(null)
  const colorSetting = colorSettingDetection(presetTheme || "")
  const mainClasses = `py-[100px] z-[2] relative ${presetTheme || ""}`
  const headerClasses = `${isHeaderBig ? layoutHeaderBigClasses : layoutHeaderClasses}
`
  const finalHeader = renderHeaderConditionally(
    headerClasses,
    header || "",
    headerElement,
  )
  const pagersRef = useRef<HTMLDivElement>(null)
  const [lineBgStyles, setLineBgStyles] = useState({})
  const [cardHeight, setCardHeight] = useState(0)
  const backgroundStyle = backgroundImageSrc
    ? { backgroundImage: `url(${backgroundImageSrc})` }
    : {}

  const componentStyles = darkTextureWrapper(presetTheme || "")

  const stepsContainerRef = useRef<HTMLDivElement>(null)

  const buttonsPopulated = buttons && buttons.length > 0

  const scrollCheck = useCallback(() => {
    if (scrollRafRef.current !== null) return
    scrollRafRef.current = requestAnimationFrame(() => {
      const el = pagersRef.current
      if (!el) {
        scrollRafRef.current = null
        return
      }
      setShowRightScroll(
        el.scrollWidth > el.clientWidth &&
          el.scrollLeft + el.clientWidth < el.scrollWidth - 5,
      )
      setShowLeftScroll(el.scrollLeft > 0)
      scrollRafRef.current = null
    })
  }, [])

  const scrollTabList = (direction: "left" | "right") => {
    pagersRef.current?.scrollBy({
      left: direction === "left" ? -100 : 100,
      behavior: "smooth",
    })
  }

  useEffect(() => {
    const el = pagersRef.current
    if (!el) return
    scrollCheck()
    el.addEventListener("scroll", scrollCheck, { passive: true })

    const ro = new ResizeObserver(() => scrollCheck())
    ro.observe(el)
    return () => {
      el.removeEventListener("scroll", scrollCheck)
      ro.disconnect()
      if (scrollRafRef.current !== null)
        cancelAnimationFrame(scrollRafRef.current)
        scrollRafRef.current = null
    }
  }, [width, scrollCheck])

  useEffect(() => {
    const el = pagersRef.current
    if (!el) return
    leftScrollButtonRef.current && (leftScrollButtonRef.current.style.left = el.offsetLeft + "px")
    rightScrollButtonRef.current && (rightScrollButtonRef.current.style.left = el.offsetWidth + el.offsetLeft - rightScrollButtonRef.current.offsetWidth + "px")
  }, [width, pagersRef, showLeftScroll, showRightScroll])

  useEffect(() => {
    if (width <= 960) {
      setCardHeight(0) // eslint-disable-line react-hooks/set-state-in-effect
      return
    }

    const container = stepsContainerRef.current
    if (!container) return

    const measureCards = () => {
      requestAnimationFrame(() => {
        const cards = container.querySelectorAll(
          ".t2-lo-macro-workflow__step-inner",
        )
        let tallest = 0
        cards.forEach(div => {
          const height = div.clientHeight
          if (height > tallest) tallest = height
        })
        setCardHeight(tallest)
      })
    }

    const observer = new ResizeObserver(measureCards)
    observer.observe(container)
    measureCards()

    return () => observer.disconnect()
  }, [width])

  const goToSlide = (index: number) => {
    setActiveSlideIndex(index)
  }

  useEffect(() => {
    if (!pagersRef?.current) return
    requestAnimationFrame(() => {
      setLineBgStyles({
        left: `${pagersRef?.current?.getBoundingClientRect()?.left}px`,
      })
    })
  }, [pagersRef])

  return (
    <div
      className={mainClasses}
      style={componentStyles}
      data-testid="layout-macro-workflow"
    >
      <div className="relative z-[3] t2-layout__content">
        <div className="w-full px-5 lg:px-20 m-auto max-w-[1440px]">
          <div className="flex flex-wrap -mx-2.5 pb-[50px] min-lg:pb-20">
            <div className="basis-full max-w-full px-2.5 ml-0 min-lg:basis-[58.333%] min-lg:max-w-[58.333%]">
              {eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}
              {finalHeader}
              {subheader && (
                <div className={layoutSubheaderClasses}>{subheader}</div>
              )}
              {description && (
                <CommonDescription
                  description={description}
                  className={
                    "t2-lo-macro-workflow__description t2-layout__description"
                  }
                />
              )}
            </div>
          </div>
        </div>
        {workflowSteps && (
          <>
            <div className="relative block">
              <div className="w-full px-5 lg:px-20 m-auto max-w-[1440px] relative h-10 overflow-x-scroll overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] !mb-20 max-sm:!mb-5 min-lg:!mb-[100px] [&::-webkit-scrollbar]:hidden" 
                ref={pagersRef} 
              >
                <div className="absolute h-0.5 -z-[1] text-[0px] left-20 right-20 top-5 [&_svg_path]:text-gray-m3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="2"
                  >
                    <path
                      stroke="currentColor"
                      strokeDasharray="2 4"
                      d="M0 1h2331"
                    />
                  </svg>
                </div>
                <div className="flex w-max overflow-x-hidden">
                  {workflowSteps?.map((step, index) => {
                    return (
                      step?.header && (
                        <div
                          key={index}
                          tabIndex={0}
                          className={`${PAGER_BASE} ${activeSlideIndex === index ? "bg-blue-trmb text-white" : "bg-gray-m3 text-gray-m9"}`}
                          onClick={() => goToSlide(index)}
                          onKeyDown={e => {
                            if (e.key === "Enter") goToSlide(index)
                          }}
                        >
                          <div className="flex items-center pointer-events-none h-5">
                            {step?.stepIcon ? (
                              <div>
                                <Icon type={step.stepIcon} size={20} />
                              </div>
                            ) : null}
                            <div className={step?.stepIcon ? "ml-3" : ""}>
                              {step?.header}
                            </div>
                          </div>
                        </div>
                      )
                    )
                  })}
                </div>
              </div>
              {showLeftScroll && (
                <button
                  type="button"
                  aria-label="Scroll tabs left"
                  ref={leftScrollButtonRef}
                  onClick={() => {
                    scrollTabList("left")
                    scrollCheck()
                  }}
                  className="absolute top-0 left-0 z-10 h-10 w-[33px] flex items-center justify-center cursor-pointer text-blue-trmb border-l-2 border-blue-trmb bg-[linear-gradient(90deg,_#fff_80%,_transparent)] pr-1 border-0 border-l-2 p-0"
                >
                  <ScrollChevron direction="left" />
                </button>
              )}
              {showRightScroll && (
                <button
                  type="button"
                  aria-label="Scroll tabs right"
                  ref={rightScrollButtonRef}
                  onClick={() => {
                    scrollTabList("right")
                    scrollCheck()
                  }}
                  className="absolute top-0 left-0 z-10 h-10 w-[33px] flex items-center justify-center cursor-pointer text-blue-trmb border-r-2 border-blue-trmb bg-[linear-gradient(270deg,_#fff_80%,_transparent)] pl-1 border-0 border-r-2 p-0"
                >
                  <ScrollChevron direction="right" />
                </button>
              )}
            </div>
          </>
        )}
        <div
          className="w-full px-5 lg:px-20 m-auto max-w-[1440px]"
          ref={stepsContainerRef}
        >
          <div className="flex flex-wrap -mx-2.5">
            <div className="basis-full max-w-full px-2.5 ml-0 min-lg:basis-2/3 min-lg:max-w-[66.666%] min-lg:ml-[16.666%]">
              <SimpleCarousel
                onSlideChange={setActiveSlideIndex}
                activeIndex={activeSlideIndex}
                dotsClassName={colorSetting}
              >
                {workflowSteps &&
                  workflowSteps?.map((step, index) => {
                    const focusParam = step?.image?.focusArea
                      ? formatFocusArea(step?.image?.focusArea)
                      : null
                    const stepImageSrc =
                      step?.image?.src + (focusParam ? `?f=${focusParam}` : "")
                    return (
                      <div
                        key={index}
                        className="block bg-blue-trmb mb-6 text-white rounded-lg max-w-[846px] max-sm:max-w-[450px] max-sm:mx-auto p-10 max-sm:p-0 [&_img]:rounded-lg [&_img]:w-full max-sm:[&_img]:rounded-t-lg max-sm:[&_img]:rounded-b-none [&_.t2-spbutton__field]:text-white"
                      >
                        <div
                          className="t2-lo-macro-workflow__step-inner flex justify-between max-sm:flex-wrap"
                          style={
                            cardHeight && width > 960
                              ? { height: `${cardHeight}px` }
                              : { height: "auto" }
                          }
                        >
                          <div className="max-sm:w-full w-1/2 mr-5 max-sm:mr-0 max-sm:mb-5">
                            <img
                              src={stepImageSrc}
                              className="aspect-video"
                              alt={step?.image?.alt}
                              loading={isLCP ? "eager" : "lazy"}
                            />
                          </div>
                          <div className="text-left w-1/2 justify-center flex-col flex max-sm:w-full max-sm:px-5 max-sm:pb-5 [&_.t2-layout__description]:text-white">
                            <h3 className="font-extrabold uppercase mb-3 text-white">
                              {step?.header}
                            </h3>
                            <CommonDescription
                              description={step?.description}
                              className="text-white leading-7"
                            />
                            {step?.stepCTA?.destination && (
                              <div className="t2-lo-macro-workflow__step-cta mt-4">
                                <SpecialButton {...step?.stepCTA} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
              </SimpleCarousel>
            </div>
          </div>
        </div>
        <div
          className={`justify-center pt-[50px] flex gap-x-4 ${colorSetting} ${CTA_OVERRIDES}`}
        >
          {buttonsPopulated &&
            buttons?.map((btn: TerraLink, index: number) => (
              <TerraLink
                key={index}
                {...btn}
                primary={index === 0 ? true : false}
              />
            ))}
        </div>
      </div>
      <div
        className="absolute top-0 right-0 w-full h-[60%] overflow-hidden z-[1] bg-white bg-[position:100%_5px] bg-no-repeat bg-[size:auto_100%]"
        style={backgroundStyle}
      ></div>
      <div className="absolute bottom-0 right-0 w-full h-[40%] overflow-hidden z-[2] bg-transparent"></div>
    </div>
  )
}
